<script setup lang="ts">
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import { useLoading } from '@composables/useLoading';
import IconHeartStraightBold from '@icons/ph_heart-straight-bold.svg';
import IconHeartStraightFill from '@icons/ph_heart-straight-fill.svg';
import type { SelectionItemOfficeOffer } from '@models/groups/selectionItem';
import { storeCallbackAndDoLogin } from '@molecules/favorite-bus-handler';
import { flashBagError } from '@tools/flashbag';
import { Logger } from '@tools/logger/logger';
import { Requests } from '@tools/requests';
import { URL_OFFER_FAVORITE_SET } from '@tools/routes/routes';
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';

const {
  isActive = false,
  offerHashId,
  offerTeamSizes: offerTeamSizesProp,
} = defineProps<{
  isActive?: boolean,
  offerHashId: string,
  offerTeamSizes?: SelectionItemOfficeOffer[],
}>();

const emit = defineEmits<{(event: 'change', newIsFavorite: boolean): Promise<void>;
}>();

const { isLoading, stopLoading, startLoading } = useLoading();

const isFavoriteActive = ref<boolean>(isActive);

const offerTeamSizes = computed<SelectionItemOfficeOffer[]>(() => offerTeamSizesProp ?? [ {
  office_offer: {
    hash_id: offerHashId,
  },
  team_size: null,
} as unknown as SelectionItemOfficeOffer ]);

const changeFavorite = async (): Promise<void> => {
  startLoading();

  try {
    await Requests.post(
      URL_OFFER_FAVORITE_SET.replace('{id}', offerHashId),
      JSON.stringify({
        favorite: !isFavoriteActive.value,
        offerTeamSizes: offerTeamSizes.value.map((offerTeamSize) => ({
          hashId: offerTeamSize.office_offer.hash_id,
          teamSize: offerTeamSize.team_size,
        })),
      }),
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      storeCallbackAndDoLogin(!isFavoriteActive.value, offerHashId);
      stopLoading();
      return;
    }

    flashBagError();
    Logger.error(error);
    stopLoading();
    return;
  }

  isFavoriteActive.value = !isFavoriteActive.value;

  await emit('change', isFavoriteActive.value);

  stopLoading();
};
</script>

<template>
  <Button
    variant="circle"
    class="favorite-button !mt-0 !h-9 !w-9"
    :class="{ 'favorite-button--active': isFavoriteActive }"
    :disabled="isLoading"
    @click.prevent="changeFavorite"
  >
    <Icon
      v-if="isFavoriteActive"
      :svg="IconHeartStraightFill"
      fill="red"
    />

    <Icon v-else :svg="IconHeartStraightBold" fill="current" />
  </Button>
</template>
